import MapView from '@arcgis/core/views/MapView';
import { MapViewInputTask } from '../framework/MapViewInputTask';
import { SurfaceDef } from '../model/SurfaceDef';
import { SurfaceBreakline } from '../model/SurfaceBreakline';
import { GroundPoint } from '../model/GroundPoint';
import { esriGeometryEngine, Graphic, Point } from 'cd-eft/utils/esriTypes';
import { nearestVertex } from '@arcgis/core/geometry/geometryEngine';
import { validNextPointSymbol, invalidNextPointSymbol } from '../resources/esriSymbols';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Multipoint from '@arcgis/core/geometry/Multipoint';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import { onToolCleanup } from '../utils/ToolUtils';

export class MoveBreaklineInputTask extends MapViewInputTask<SurfaceBreakline> {
    private readonly surfaceDef: SurfaceDef;
    protected breaklineToEdit?: SurfaceBreakline;
    private selectedVertexIndex: number | null = null;
    private feedbackLayer: GraphicsLayer = new GraphicsLayer({ title: 'moveFeedbackLayer', listMode: 'hide' });
    private allGroundPoints: GroundPoint[] = [];
    private allGroundPointsMP: Multipoint = new Multipoint();
    private readonly tempBreaklinesLayer: GraphicsLayer = new GraphicsLayer({ title: 'tempBreaklinesLayer' });

    private __highlightHandle: any;

    constructor(mapView: MapView, surfaceDef: SurfaceDef) {
        super(mapView);
        this.surfaceDef = surfaceDef;
    }

    protected onSetup(): void {
        super.onSetup();
        this.cursor = 'pointer';

        if (this.__highlightHandle) {
            this.__highlightHandle.remove();
            this.__highlightHandle = undefined;
        }

        this.tempBreaklinesLayer.removeAll();

        for (const breakline of this.surfaceDef.breaklines) {
            const graphic = breakline.generateGraphic(this.mapView.spatialReference);
            graphic.symbol = new SimpleLineSymbol({ width: 3, color: 'red' });
            this.tempBreaklinesLayer.add(graphic);
        }

        this.mapView.map.add(this.tempBreaklinesLayer);

        this.allGroundPoints = this.surfaceDef.getAllGroundPoints();
        const multipoint: Multipoint = new Multipoint({ spatialReference: this.mapView.spatialReference });
        for (const point of this.allGroundPoints) {
            multipoint.addPoint(point.generateGeometry(this.mapView.spatialReference));
        }
        this.allGroundPointsMP = multipoint;
        this.mapView.map.add(this.feedbackLayer);
    }

    protected onCleanup(): void {
        super.onCleanup();
        this.mapView.map.remove(this.feedbackLayer);
        this.feedbackLayer.removeAll();
        this.selectedVertexIndex = null;
        this.breaklineToEdit = undefined;

        onToolCleanup(this.__highlightHandle, this.tempBreaklinesLayer, this.mapView);
    }

    protected async onImmediateClick(e: __esri.ViewImmediateClickEvent): Promise<void> {
        super.onImmediateClick(e);
        const mapPoint = e.mapPoint;

        if (!this.breaklineToEdit) {
            // First click: select breakline
            const hitResult = await this.mapView.hitTest(e, { include: this.mapView.map.layers });
            const graphic = hitResult.results.find((r) => r.graphic?.attributes?.eft_type === 'SurfaceBreakline')
                ?.graphic;
            if (!graphic) return;
            graphic.symbol = new SimpleLineSymbol({ width: 3, color: 'blue' });
            this.breaklineToEdit = graphic.attributes as SurfaceBreakline;
            this.feedbackLayer.add(graphic.clone());
            return;
        }

        if (this.selectedVertexIndex === null) {
            // Second click: select vertex to move
            const snapResult = this.findNearestVertexOnBreakline(mapPoint);
            if (!snapResult) return;
            this.selectedVertexIndex = snapResult.index;
            this.drawHighlightVertex(snapResult.point);
            return;
        }

        // Third click: replace vertex with new GroundPoint
        const newGroundPoint = this.findNearestGroundPoint(mapPoint);
        if (!newGroundPoint) return;
        const isDuplicate = (this.breaklineToEdit.groundPoints.findIndex((p) => p === newGroundPoint) === -1) ? false : true;
        const nextPoint = newGroundPoint.generateGeometry(this.mapView.spatialReference);
        const nextPointGraphic = new Graphic({
            geometry: nextPoint,
            symbol: isDuplicate ? invalidNextPointSymbol : validNextPointSymbol,
        });
        this.feedbackLayer.removeAll();
        this.feedbackLayer.add(nextPointGraphic);
        if (isDuplicate) return;
        const updated = new SurfaceBreakline(this.breaklineToEdit.breaklineId);
        updated.groundPoints.push(...this.breaklineToEdit.groundPoints);
        updated.groundPoints[this.selectedVertexIndex] = newGroundPoint;
        if (await this.surfaceDef.checkBreakLineCrosses(updated, this.mapView)) {
            this.feedbackLayer.removeAll();
            this.selectedVertexIndex = null;
            return;
        }
        this.surfaceDef.breaklinesSyncSet.updateItem(this.breaklineToEdit, updated, true);

        this.completeNow(updated);
    }

    private findNearestVertexOnBreakline(mapPoint): { index: number; point: __esri.Point } | null {
        if (!this.breaklineToEdit) return null;
        const sr = this.mapView.spatialReference;
        const polyline = this.breaklineToEdit.generateGeometry(sr);
        const res = nearestVertex(polyline, mapPoint);
        if (res.distance < 3) {
            return { index: res.vertexIndex, point: res.coordinate };
        }
        return null;
    }

    private findNearestGroundPoint(mapPoint: Point): GroundPoint {
        const findResult = esriGeometryEngine.nearestVertex(this.allGroundPointsMP, mapPoint);
        let result;
        if (findResult.distance < 11) {
            result = this.allGroundPoints[findResult.vertexIndex];
        }
        return result;
    }

    private drawHighlightVertex(point: __esri.Point) {
        this.feedbackLayer.removeAll();
        const graphic = new Graphic({ geometry: point, symbol: validNextPointSymbol });
        graphic.attributes = { type: 'selectedVertex' };
        this.feedbackLayer.add(graphic);
    }
}
